import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMeetings } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Plus, MagnifyingGlass, Calendar, Phone, Video, Users } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';

export default function MeetingsPage() {
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMeetings();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = meetings.filter(m => 
        m.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.client_company && m.client_company.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredMeetings(filtered);
    } else {
      setFilteredMeetings(meetings);
    }
  }, [searchQuery, meetings]);

  const fetchMeetings = async () => {
    try {
      const response = await getMeetings();
      setMeetings(response.data);
      setFilteredMeetings(response.data);
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'call': return <Phone size={16} />;
      case 'video': return <Video size={16} />;
      case 'in-person': return <Users size={16} />;
      default: return <Calendar size={16} />;
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'prospecting': return 'bg-gray-100 text-gray-700';
      case 'qualification': return 'bg-blue-50 text-blue-700';
      case 'proposal': return 'bg-amber-50 text-amber-700';
      case 'negotiation': return 'bg-purple-50 text-purple-700';
      case 'closed': return 'bg-green-50 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-entrance" data-testid="meetings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1F]">
            Meetings
          </h1>
          <p className="text-[#86868B] mt-1">
            {meetings.length} meeting{meetings.length !== 1 ? 's' : ''} recorded
          </p>
        </div>
        <Link to="/meetings/new">
          <Button className="btn-primary h-11 px-6 rounded-xl" data-testid="new-meeting-btn">
            <Plus size={20} className="mr-2" />
            Record Meeting
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Search by client name or company..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input-base h-12 pl-12 rounded-xl"
          data-testid="meetings-search"
        />
      </div>

      {/* Meetings List */}
      {filteredMeetings.length === 0 ? (
        <div className="card-base p-12 text-center">
          <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-[#1D1D1F]">
            {searchQuery ? 'No meetings found' : 'No meetings yet'}
          </h3>
          <p className="text-[#86868B] mt-2">
            {searchQuery ? 'Try a different search term' : 'Record your first client meeting to get started'}
          </p>
          {!searchQuery && (
            <Link to="/meetings/new">
              <Button className="btn-secondary mt-6 rounded-xl" data-testid="record-first-meeting-btn">
                <Plus size={18} className="mr-2" />
                Record Meeting
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredMeetings.map((meeting) => (
            <Link 
              key={meeting.id} 
              to={`/meetings/${meeting.id}`}
              className="card-base p-6 block hover:shadow-md transition-shadow"
              data-testid={`meeting-card-${meeting.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-[#1D1D1F] truncate">
                      {meeting.client_name}
                    </h3>
                    <Badge className={`${getStageColor(meeting.deal_stage)} rounded-xl text-xs capitalize`}>
                      {meeting.deal_stage || 'N/A'}
                    </Badge>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#86868B]">
                    {meeting.client_company && (
                      <span>{meeting.client_company}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {format(parseISO(meeting.meeting_date), 'MMM d, yyyy')}
                    </span>
                    <span className="flex items-center gap-1 capitalize">
                      {getMeetingTypeIcon(meeting.meeting_type)}
                      {meeting.meeting_type}
                    </span>
                    {meeting.deal_value && (
                      <span className="font-medium text-[#34C759]">
                        ${meeting.deal_value.toLocaleString()}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-[#86868B] mt-3 line-clamp-2">
                    {meeting.key_points}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
